import { useEffect, useState } from 'react';
import AppBarSimple from '../components/AppBarSimple';
import Footer from '../components/Footer';
import Loader from '../components/Loader';
import CustomLink from '../util/CustomLink';
import { useAuth } from '../context/AuthContext';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import * as EmailValidator from 'email-validator';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const [state, setState] = useState({
    email: '',
    password: '',
    isEmailInvalid: false,
    isPasswordInvalid: false,
    isAuthError: false,
  });
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const redirectUrl = new URLSearchParams(location.search).get('redirect');
  const [loaded, setLoaded] = useState(false);
  const [recover, setRecover] = useState(false);
  const { t } = useTranslation(['login', 'common']);

  const { user, loading, legacyWebAccess, signInWithEmail } = useAuth();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');
    setError(null);

    if (window.electron && !navigator.onLine) {
      setError(t('login:errors.offline'));
      return;
    }

    if (!validateEmail(email)) {
      setState({ ...state, isEmailInvalid: true });
      setEmailError(t('login:errors.invalidEmail'));
      return;
    }

    if (!validatePassword(password)) {
      setState({ ...state, isPasswordInvalid: true });
      setPasswordError(t('login:errors.invalidPassword'));
      return;
    }

    signInWithEmail(email, password, setError);
  };

  const recheckEmail = (email) => {
    setError(null);
    if (state.isEmailInvalid && validateEmail(email)) {
      setState({ ...state, isEmailInvalid: false });
    }
  };

  const recheckPassword = (password) => {
    setError(null);
    if (state.isPasswordInvalid && validatePassword(password)) {
      setState({ ...state, isPasswordInvalid: false });
    }
  };

  const validateEmail = (email) => {
    return EmailValidator.validate(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (user && !redirectUrl) {
      navigate(legacyWebAccess ? '/dashboard' : '/minha-conta');
    } else if (user && redirectUrl) {
      navigate(`/${redirectUrl}`);
    } else {
      setLoaded(true);
    }
  }, [user, loading, legacyWebAccess, navigate, redirectUrl]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBarSimple />
      {loaded ? (
        !recover ? (
          <Container component="main" maxWidth="xs" sx={{ mb: 8 }}>
            <CssBaseline />
            <Box sx={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5">
                {t('login:signIn.title')}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="email"
                  label={t('login:fields.email')}
                  name="email"
                  color="secondary"
                  autoComplete="email"
                  onChange={(e) => recheckEmail(e.target.value)}
                  error={state.isEmailInvalid}
                  helperText={state.isEmailInvalid ? emailError : ' '}
                />
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  name="password"
                  label={t('login:fields.password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  color="secondary"
                  autoComplete="current-password"
                  onChange={(e) => recheckPassword(e.target.value)}
                  error={state.isPasswordInvalid}
                  helperText={state.isPasswordInvalid ? passwordError : ' '}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  onClick={() => { setRecover(true); }}
                  style={{ cursor: 'pointer' }}
                >
                  {t('login:signIn.forgotPassword')}
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 0 }}
                  data-testid="btn-entrar"
                >
                  {t('login:signIn.submit')}
                </Button>

                {error ? (
                  <Typography variant="caption" color="error">
                    {error}
                  </Typography>
                ) : (
                  <Box height={24} />
                )}

                <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('login:signIn.noAccountQuestion')}
                  </Typography>
                  <CustomLink href="https://gamevision.pro/">
                    {t('login:signIn.noAccountInstruction')}
                  </CustomLink>
                </Box>
              </Box>
            </Box>
          </Container>
        ) : (
          <RecuperarSenha setRecover={setRecover} />
        )
      ) : (
        <Loader vh={80} />
      )}
      <Footer />
    </Box>
  );
};

export default Login;

const RecuperarSenha = ({ setRecover }) => {
  const [state, setState] = useState({ email: '', isEmailInvalid: false });
  const [emailError, setEmailError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { sendPasswordReset } = useAuth();
  const { t } = useTranslation('login');

  const handleClose = (e) => {
    e.preventDefault();
    setRecover(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (window.electron && !navigator.onLine) {
      setEmailError(t('errors.offline'));
      return;
    }

    const data = new FormData(event.currentTarget);
    const email = data.get('email');

    if (!validateEmail(email)) {
      setState({ ...state, isEmailInvalid: true });
      setEmailError(t('errors.invalidEmail'));
      return;
    }

    sendPasswordReset(email);
    setSuccess(true);
  };

  const recheckEmail = (email) => {
    if (state.isEmailInvalid && validateEmail(email)) {
      setState({ ...state, isEmailInvalid: false });
    }
  };

  const validateEmail = (email) => {
    return EmailValidator.validate(email);
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ mb: 8 }}>
      <CssBaseline />
      <Box sx={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {t('recover.title')}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="email"
            label={t('fields.email')}
            name="email"
            color="secondary"
            autoComplete="email"
            autoFocus
            onChange={(e) => recheckEmail(e.target.value)}
            error={state.isEmailInvalid}
            helperText={state.isEmailInvalid ? emailError : ' '}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 0 }}
          >
            {t('recover.submit')}
          </Button>
          <Box height={24} />

          <Typography
            onClick={() => { setRecover(false); }}
            style={{ cursor: 'pointer' }}
          >
            {t('recover.backToLogin')}
          </Typography>
        </Box>
      </Box>
      <Dialog
        open={success}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('recover.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('recover.dialog.descriptionLine1')}
            <br />
            <br />
            {t('recover.dialog.descriptionLine2')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            {t('recover.dialog.cta')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
